// Report Builder defaults: where a new element goes, the table's columns, the report title
// and the rows the table shows (V.28, V.52).

export const ELEMENT_MARGIN = 40;
export const ELEMENT_GAP = 12;

/**
 * Position for a new element of height `h`: stacked below everything already on the page,
 * so elements never overlap by default. When the page is full it goes below the last
 * element anyway (the page scrolls) rather than on top of it.
 */
export function nextElementPosition(elements) {
  const list = (elements || []).filter((e) => e && Number.isFinite(e.y) && Number.isFinite(e.h));
  if (!list.length) return { x: ELEMENT_MARGIN, y: ELEMENT_MARGIN };
  const bottom = Math.max(...list.map((e) => e.y + e.h));
  return { x: ELEMENT_MARGIN, y: bottom + ELEMENT_GAP };
}

const COORD_ORDER = ['x', 'y', 'easting', 'northing', 'lon', 'lng', 'longitude', 'lat', 'latitude'];

/**
 * Table columns for a set of rows: coordinate columns first (easting/northing, x/y) when
 * present, then the other attributes, at most `max` in all. Internal `_` keys are skipped.
 */
export function tableColumns(rows, max = 6) {
  const first = rows && rows[0];
  if (!first) return [];
  const keys = Object.keys(first).filter((k) => !k.startsWith('_'));
  const lower = (k) => k.toLowerCase();
  const coords = [];
  COORD_ORDER.forEach((c) => {
    const k = keys.find((key) => lower(key) === c);
    if (k && !coords.includes(k)) coords.push(k);
  });
  const rest = keys.filter((k) => !coords.includes(k));
  return [...coords, ...rest].slice(0, max);
}

/** Title / PDF name: the active project, else the dataset, else the demo name (demo mode only). */
export function reportTitle({ project, datasetName, demoEnabled = false, demoName = 'USGS Cape Cod Bay 2019' }) {
  return project?.name || datasetName || (demoEnabled ? demoName : 'Untitled project');
}

/**
 * Rows for the table element: the first visible layer that has features (imported layers,
 * then results, then the survey lines), at most `limit`, with the feature's x/y added when
 * the properties do not carry them.
 */
export function reportTableRows(layerGroups, fallbackFeatures = [], limit = 25) {
  let features = null;
  for (const group of layerGroups || []) {
    const hit = (group || []).find((l) => l && l.visible !== false && l.geojson?.features?.length);
    if (hit) { features = hit.geojson.features; break; }
  }
  if (!features) features = fallbackFeatures || [];
  return features.slice(0, limit).map((f) => {
    const props = { ...(f.properties || {}) };
    const c = f.geometry?.type === 'Point' ? f.geometry.coordinates : null;
    if (c && Number.isFinite(c[0]) && Number.isFinite(c[1])) {
      const has = (name) => Object.keys(props).some((k) => k.toLowerCase() === name);
      const hasX = ['x', 'easting', 'lon', 'lng', 'longitude'].some(has);
      const hasY = ['y', 'northing', 'lat', 'latitude'].some(has);
      const out = {};
      if (!hasX) out.x = c[0];
      if (!hasY) out.y = c[1];
      return { ...out, ...props };
    }
    return props;
  });
}
